import   React            from 'react';

import   classNames       from 'classnames';

import   styles           from './BetaApplicationList.module.scss';

import { BetaApplication                  ,
         BETA_AGE_RANGE_LABELS            ,
         BETA_EQUIPMENT_LABELS            ,
         BETA_OPERATING_SYSTEM_LABELS     ,
         BETA_APPLICATION_STATUS_LABELS   ,
         getBetaActivityLabel             }  from '../../../dataTypes/betaApplicationManagment/types';

import { formatDateOrDash                 }  from '../../../dataTypes/adminManagment/utils';

interface BetaApplicationSheetProps {
  application : BetaApplication;
  onClose     : () => void;
  onAccept    : (application: BetaApplication) => void;
  onRefuse    : (application: BetaApplication) => void;
  onWaitlist  : (application: BetaApplication) => void;
}

const Field = ({ label , value }: { label: string , value: React.ReactNode }) => (
  <div className={classNames(styles.field)}>
    <span className={classNames(styles.fieldLabel)}>{label}</span>
    <span className={classNames(styles.fieldValue)}>{value === '' || value === null || value === undefined ? '—' : value}</span>
  </div>
);

// Fiche d'une candidature : lecture seule, avec les mêmes décisions que la liste.
export const BetaApplicationSheet = ({ application ,
                                       onClose     ,
                                       onAccept    ,
                                       onRefuse    ,
                                       onWaitlist  }: BetaApplicationSheetProps) => {

  // Acceptation possible seulement une fois l'adresse e-mail confirmée par le candidat
  const canAccept   = ( application.status === 'pending' || application.status === 'waitlist' ) && application.emailValidated;
  const canRefuse   = application.status === 'pending' || application.status === 'waitlist';
  const canWaitlist = application.status === 'pending';

  const equipments = Object.entries ( application.equipments );

  return (
    <div className={classNames(styles.sheet)}>

      <div className={classNames(styles.sheetHead)}>
        <div className={classNames(styles.headTitleBlock)}>
          <span className={classNames(styles.headTitle)}>
            {`${application.firstname} ${application.lastname}`.trim()}
          </span>
          <span className={classNames(styles.headSubtitle)}>
            {`Candidature n° ${application.id} — ${BETA_APPLICATION_STATUS_LABELS[application.status] ?? application.status}${application.isPromotion ? ' — PROMOTION d\'un compte existant' : ''}`}
          </span>
        </div>

        <div className={classNames(styles.sheetActions)}>
          {canAccept   && <button className={classNames(styles.btn, styles.btnAccept)}  onClick={() => onAccept(application)}>Accepter</button>}
          {canWaitlist && <button className={classNames(styles.btn, styles.btnWait)}    onClick={() => onWaitlist(application)}>Liste d'attente</button>}
          {canRefuse   && <button className={classNames(styles.btn, styles.btnRefuse)}  onClick={() => onRefuse(application)}>Refuser</button>}
          <button className={classNames(styles.btn, styles.btnNeutral)} onClick={onClose}>Retour à la liste</button>
        </div>
      </div>

      <div className={classNames(styles.card)}>
        <span className={classNames(styles.cardTitle)}>Candidat</span>
        <div className={classNames(styles.grid)}>
          <Field label="Prénom"        value={application.firstname} />
          <Field label="Nom"           value={application.lastname} />
          <Field label="E-mail"        value={application.email} />
          <Field label="Vérification de l'e-mail"
                 value={application.emailValidated
                          ? `Confirmé le ${formatDateOrDash(application.emailValidationDate)}${application.isPromotion ? ' (e-mail de son compte existant)' : ''}`
                          : 'Pas encore confirmé par le candidat'} />
          {
           application.isPromotion &&
           <Field label="Compte Kleon existant"
                  value={`n° ${application.existingUserId} — l'acceptation en fait un compte créateur (mot de passe conservé)`} />
          }
          <Field label="Téléphone"     value={application.phoneNumber} />
          <Field label="Tranche d'âge" value={BETA_AGE_RANGE_LABELS[application.ageRange] ?? application.ageRange} />
          <Field label="Activité"      value={getBetaActivityLabel(application)} />
          <Field label="Nom public"    value={application.publicName} />
          <Field label="Organisation"  value={application.organization} />
          <Field label="Site web / réseau social"
                 value={application.website !== ''
                          ? <a href={application.website} target="_blank" rel="noopener noreferrer nofollow">{application.website}</a>
                          : ''} />
        </div>
      </div>

      <div className={classNames(styles.card)}>
        <span className={classNames(styles.cardTitle)}>Description</span>
        <span className={classNames(styles.description)}>{application.description || '—'}</span>
      </div>

      <div className={classNames(styles.card)}>
        <span className={classNames(styles.cardTitle)}>Matériel disponible</span>
        {
         equipments.length === 0
         ? <span>—</span>
         : equipments.map ( ([ type , details ]) => (
             <div key={type} className={classNames(styles.equipment)}>
               <strong>{BETA_EQUIPMENT_LABELS[type] ?? type}</strong>
               <span>{`Modèle : ${details.model || '—'}`}</span>
               <span>
                 {`Système : ${details.operatingSystems.length > 0
                                 ? details.operatingSystems.map ( os => BETA_OPERATING_SYSTEM_LABELS[os] ?? os ).join ( ', ' )
                                 : '—'}`}
               </span>
             </div>
           ))
        }
      </div>

      <div className={classNames(styles.card)}>
        <span className={classNames(styles.cardTitle)}>Suivi</span>
        <div className={classNames(styles.grid)}>
          <Field label="Candidature déposée le" value={formatDateOrDash(application.creationDate)} />
          <Field label="CGU acceptées le"       value={formatDateOrDash(application.cguAcceptedDate)} />
          <Field label="Adresse IP"             value={application.ipAddress} />
          <Field label="Décision prise le"      value={formatDateOrDash(application.reviewedDate)} />
          <Field label="Décision prise par (compte n°)" value={application.reviewedByAdminId ?? ''} />
          <Field label="Compte créateur créé (n°)"      value={application.createdAdminId ?? ''} />
        </div>
      </div>

    </div>
  );
};

export default BetaApplicationSheet;
